// The divining (Figma Horoscope / Divining): while the horoscope is written
// the whole window gives way to the chart, dimmed, with the astrolabe in
// moonlight silver at its centre. The limb with its degree ticks and the
// ring of twelve houses turn slowly in opposite directions, a rule sweeps
// the dial, the sun breathes at the centre, and a line below says what the
// stars are doing. Once written: "The stars have spoken." and Reveal
// Horoscope, which closes it on the horoscope underneath. Under reduced
// motion nothing turns; the line still changes.
import { useEffect, useRef, useState } from 'react';
import type { Sign, SignConstellation } from '../../systems/api/types';
import { Button } from '../components/Button';
import { fourPoint } from '../sky/ConstellationArt';
import { useReducedMotion } from '../sky/reducedMotion';

const C = 160;
/** The dotted orbits inside the houses. */
const ORBITS = [44, 72, 100];
const PHRASE_MS = 1500;

interface Props {
  signs: Record<Sign, SignConstellation>;
  /** Written: the stars have spoken. */
  spoken: boolean;
  /** Revealed: the divining fades away. */
  closing: boolean;
  onReveal: () => void;
}

export function Divining({ signs, spoken, closing, onReveal }: Props) {
  const phrases = [
    `Setting ${signs.sun.word} in your sun…`,
    `Turning ${signs.moon.word} toward the moon…`,
    `Watching ${signs.rising.word} rise…`,
    'Setting the type…',
    'The stars are taking their time…',
  ];
  const still = useReducedMotion();
  const [phrase, setPhrase] = useState(0);
  const reveal = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (spoken || phrase >= phrases.length - 1) return;
    const id = setTimeout(() => setPhrase((p) => p + 1), PHRASE_MS);
    return () => clearTimeout(id);
  }, [phrase, phrases.length, spoken]);

  useEffect(() => {
    if (spoken) reveal.current?.focus({ preventScroll: true });
  }, [spoken]);

  const classes = ['divining', spoken ? 'is-spoken' : '', closing ? 'is-closing' : '', still ? 'is-still' : ''].join(' ').trim();
  return (
    <div className={classes} role="dialog" aria-modal="true" aria-label={spoken ? 'The stars have spoken' : 'Your horoscope is being written'}>
      <div className="divining-mask" />
      <div className="divining-body">
        <svg viewBox="0 0 320 320" className="dial" aria-hidden="true">
          <g className="dial-limb">
            <circle cx={C} cy={C} r="150" />
            {Array.from({ length: 72 }, (_, i) => {
              const t = (i * 5 * Math.PI) / 180;
              const r0 = i % 6 === 0 ? 139 : 144;
              return (
                <line
                  key={i}
                  x1={C + r0 * Math.cos(t)}
                  y1={C + r0 * Math.sin(t)}
                  x2={C + 150 * Math.cos(t)}
                  y2={C + 150 * Math.sin(t)}
                  className={i % 6 === 0 ? 'tick-major' : undefined}
                />
              );
            })}
          </g>
          <g className="dial-houses">
            <circle cx={C} cy={C} r="136" />
            <circle cx={C} cy={C} r="120" />
            {Array.from({ length: 12 }, (_, i) => {
              const t = ((i * 30 + 15) * Math.PI) / 180;
              return <line key={i} x1={C + 120 * Math.cos(t)} y1={C + 120 * Math.sin(t)} x2={C + 136 * Math.cos(t)} y2={C + 136 * Math.sin(t)} />;
            })}
            {Array.from({ length: 12 }, (_, i) => {
              const t = (i * 30 * Math.PI) / 180;
              return <path key={i} d={fourPoint(2.6, 0.6)} transform={`translate(${C + 128 * Math.cos(t)} ${C + 128 * Math.sin(t)})`} className="house-star" />;
            })}
          </g>
          {ORBITS.map((r) => (
            <circle key={r} cx={C} cy={C} r={r} className="dial-orbit" />
          ))}
          <g className="dial-rule">
            <line x1={C} y1={C} x2={C} y2={C - 150} />
            <circle cx={C} cy={C - 150} r="2.5" />
          </g>
          <g className="dial-sun">
            <circle cx={C} cy={C} r="13" />
            {Array.from({ length: 12 }, (_, i) => {
              const t = ((i * 30 + 15) * Math.PI) / 180;
              return <line key={i} x1={C + 17 * Math.cos(t)} y1={C + 17 * Math.sin(t)} x2={C + 23 * Math.cos(t)} y2={C + 23 * Math.sin(t)} />;
            })}
          </g>
        </svg>
        {spoken ? (
          <div className="divining-spoken">
            <p className="divining-spoken-line">The stars have spoken.</p>
            <Button ref={reveal} variant="primary" className="divining-reveal" onClick={onReveal}>
              Reveal Horoscope
            </Button>
          </div>
        ) : (
          <p className="divining-line" role="status" aria-live="polite" key={phrase}>
            {phrases[phrase]}
          </p>
        )}
      </div>
    </div>
  );
}
